import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { useState, useEffect, useMemo } from 'react';
import { useMount } from 'react-use-v13';
import PipelineV3TraitSelector, {
  ISelectedPipeline,
} from 'react-scale/corp/components/UI/PipelineV3TraitSelector';
import {
  Text,
  Spinner,
  Checkbox,
  Callout,
  Flex,
  Icon,
  Tabs,
  Box,
  Tooltip,
  DataTable,
  Code,
} from '@scale/scaleui-radix';
import {
  PipelineV3AllTraitsByPipelineResponse,
  ITrait,
} from '@scale/shared/pipelinev3/requestTypes';
import { getAllTraits } from 'corp/lib/api/pipelineV3';
import { PipelineV3Name } from '@scale/shared-types';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { useCompassCard } from '@scale/compass-ui/store';
import { getSingleColumnOfSubtype, columnsFromRecord } from '@scale/compass-shared/utils';
import { ColumnSubType } from '@scale/compass-shared/types';
import type { TaskActionsContext } from '@scale/genai-compass-shared/cards/task_actions';
import { faCircleExclamation, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { createColumnHelper } from '@tanstack/react-table';

type TaskActionsTabs = 'standard' | 'dynamic';

const TaskActionsCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { card, updateCardContext } = useCompassCard(index);
  const context = card.defaultContext as TaskActionsContext;
  const { retrievedOutput: priorCardRetrievedOutput } = useCompassCard(index - 1);

  // Initialize currentTab based on context.traitName
  const [currentTab, setCurrentTab] = useState<TaskActionsTabs>(
    context.traitName === 'bespokeRecallToReviewLevel' ? 'dynamic' : 'standard',
  );

  const [selectedTrait, setSelectedTrait] = useState<string>(context.traitName);
  const [traitParamValues, setTraitParamValues] = useState<Record<string, any>>(
    context.traitParams,
  );
  const [allTraitsByPipeline, setAllTraitsByPipeline] = useState<
    PipelineV3AllTraitsByPipelineResponse | undefined
  >(undefined);
  const [selectedPipeline, setSelectedPipeline] = useState<ISelectedPipeline | undefined>(
    undefined,
  );
  const [overrideCurrentClaims, setOverrideCurrentClaims] = useState<boolean>(
    context.overrideCurrentClaims === undefined ? false : context.overrideCurrentClaims,
  );

  const updateTraitParams = (value: any, name: string) => {
    setTraitParamValues({ ...traitParamValues, [name]: value === '' ? undefined : value });
  };

  useMount(async () => {
    const traits = await getAllTraits();
    setAllTraitsByPipeline(traits);
  });

  useEffect(() => {
    if (allTraitsByPipeline) {
      const genAIPipeline: ITrait | undefined = allTraitsByPipeline.traits.find(
        (trait: ITrait) => trait.pipeline === PipelineV3Name.ChatPipeline,
      );

      if (genAIPipeline) {
        setSelectedPipeline({ ...genAIPipeline, id: genAIPipeline.pipeline });
      }
    }
  }, [allTraitsByPipeline]);

  // Update selectedTrait based on the current tab
  useEffect(() => {
    if (currentTab === 'standard') {
      setSelectedTrait('');
    } else if (currentTab === 'dynamic') {
      setSelectedTrait('bespokeRecallToReviewLevel');
    }
  }, [currentTab]);

  // Update currentTab based on selectedTrait
  useEffect(() => {
    if (selectedTrait === 'bespokeRecallToReviewLevel') {
      setCurrentTab('dynamic');
    } else {
      setCurrentTab('standard');
    }
  }, [selectedTrait]);

  useEffect(() => {
    const updatedContext: TaskActionsContext = {
      ...context,
      traitName: selectedTrait,
      traitParams: traitParamValues,
      overrideCurrentClaims,
    };
    updateCardContext(updatedContext);
  }, [selectedTrait, traitParamValues, overrideCurrentClaims]);

  const isLoaded = !!selectedPipeline;

  // client side: get prior card data and check if it has a column with name `taskId`
  const { taskIdCol, error: taskIdColError } = useMemo(() => {
    try {
      const columns = columnsFromRecord(priorCardRetrievedOutput?.response?.data);
      const taskIdCol = getSingleColumnOfSubtype(columns, ColumnSubType.TASK_ID);

      // no columns are detected as taskId
      if (!taskIdCol) {
        return {
          taskIdCol: undefined,
          error:
            'Please add only a single column with name taskId containing task ids to use this action.',
        };
      }

      // exactly 1 column is detected as taskId
      return {
        taskIdCol,
        error: null,
      };
    } catch (error) {
      return {
        taskIdCol: undefined,
        error: `Please ensure there is only one column containing task ids.`,
      };
    }
  }, [priorCardRetrievedOutput]);

  if (taskIdColError) {
    return (
      <div className="flex flex-row gap-36 justify-between">
        <SanitizedMarkdown text={taskIdColError} />
      </div>
    );
  }

  const standardContent = (
    <>
      {!isLoaded && <Spinner size="2" />}
      {isLoaded && (
        <div className="flex flex-row gap-36 justify-between">
          <div>
            {priorCardRetrievedOutput && !taskIdCol && (
              <Callout.Root
                color="red"
                variant="soft"
                role="alert"
                size="2"
                className="items-center py-2 mb-3"
              >
                <Callout.Icon>
                  <Icon icon={faCircleExclamation} label="info" size={1} />
                </Callout.Icon>
                <Flex direction="column">
                  {/* <Text as="p" size="2"> */}
                  <div>
                    <SanitizedMarkdown
                      text={`**No taskId column found in the prior card.** You **must** name a column \`taskId\` in the prior card to use this action.`}
                      innerStyle={' '}
                    />
                  </div>
                  {/* </Text> */}
                </Flex>
              </Callout.Root>
            )}
            <SanitizedMarkdown
              text={`**This will apply the selected action to the tasks from column \`taskId\` in the prior card.** \nAn async corp job will be kicked off upon submission. DO NOT retry the action with the same data. The corp job will have an ETA and also report any errors that come up.`}
            />
          </div>
          <div className="flex flex-col gap-4">
            <PipelineV3TraitSelector
              selectedTrait={selectedTrait}
              setSelectedTrait={setSelectedTrait}
              selectedPipeline={selectedPipeline}
              traitParamValues={traitParamValues}
              updateTraitParams={updateTraitParams}
              clearTraitParams={() => setTraitParamValues({})}
            />
            <Text className="flex flex-row gap-2">
              <Checkbox
                checked={overrideCurrentClaims}
                onCheckedChange={(value: boolean) => setOverrideCurrentClaims(value)}
              />
              Override current claims if they exist
            </Text>
          </div>
        </div>
      )}
    </>
  );

  const dynamicContent = (
    <div className="flex flex-row gap-36 justify-between">
      <div className="w-full">
        {priorCardRetrievedOutput && !taskIdCol && (
          <Callout.Root
            color="red"
            variant="soft"
            role="alert"
            size="2"
            className="items-center py-2 mb-3"
          >
            <Callout.Icon>
              <Icon icon={faCircleExclamation} label="info" size={1} />
            </Callout.Icon>
            <Flex direction="column">
              <div>
                <SanitizedMarkdown
                  text={`**No taskId column found in the prior card.** You **must** name a column \`taskId\` in the prior card to use this action.`}
                  innerStyle={' '}
                />
              </div>
            </Flex>
          </Callout.Root>
        )}
        <SanitizedMarkdown
          text={`This will apply the **chat task recall to review level** action with custom params per row. Ensure the following columns are present in the prior card to specify custom params per row:`}
        />

        <div className="mt-4 mb-4">
          <ParametersTable />
        </div>

        <SanitizedMarkdown
          text={`An async corp job will be kicked off upon submission. DO NOT retry the action with the same data. The corp job will have an ETA and also report any errors that come up.`}
        />

        <div className="mt-4">
          <Text className="flex flex-row gap-2">
            <Checkbox
              checked={overrideCurrentClaims}
              onCheckedChange={(value: boolean) => setOverrideCurrentClaims(value)}
            />
            Override current claims if they exist
          </Text>
        </div>
      </div>
    </div>
  );

  return (
    <Tabs.Root value={currentTab} onValueChange={value => setCurrentTab(value as TaskActionsTabs)}>
      <Tabs.List>
        <Tabs.Trigger value="standard">
          <Flex direction="row" gap="2">
            Standard
            <Tooltip content="Take one action on all taskIds in the previous rows">
              <Icon icon={faInfoCircle} label="info" size={1} />
            </Tooltip>
          </Flex>
        </Tabs.Trigger>
        <Tabs.Trigger value="dynamic">
          <Flex direction="row" gap="2">
            Dynamic
            <Tooltip content="Take custom actions per row (taskId) based on corresponding column data">
              <Icon icon={faInfoCircle} label="info" size={1} />
            </Tooltip>
          </Flex>
        </Tabs.Trigger>
      </Tabs.List>
      <Box pt="4">
        <Tabs.Content value="standard">{standardContent}</Tabs.Content>
        <Tabs.Content value="dynamic">{dynamicContent}</Tabs.Content>
      </Box>
    </Tabs.Root>
  );
};

const ParametersTable = () => {
  const paramMetadata = useMemo(
    () => ({
      taskId: {
        description: 'The task id to apply the action to',
        required: true,
      },
      destinationReviewLevel: {
        description: 'The review level to recall to.',
        required: true,
      },
      recallingFromReviewLevel: {
        description:
          'If this field is set, only tasks at this level will be moved even if tasks from outside this review level are provided as input.',
        required: false,
      },
      recallCompletedTasks: {
        description: 'This tool by default ignores completed tasks. Set this to true to override.',
        required: false,
      },
      clearResponses: {
        description:
          'If this field is set to true, the current response will not be propagated to the new reviewLevel.',
        required: false,
      },
      forceRecall: {
        description:
          'By default this action will fail if there is unfinished work that will be invalidated (thrown out), Set this to true to override.',
        required: false,
      },
      recallReason: {
        description: 'Add a note for the reason of the recall.',
        required: false,
      },
      clearWorkerAssignment: {
        description:
          'Clears routing based worker assignment (will also clear exclusive workers and minutes to claim for the worker). By default this is set to false, set this to true to override.',
        required: false,
      },
      messageForContributorText: {
        description:
          'A message that contributors will see when receiving the recalled task in their queue. This should be used to display contributor-friendly versions of the recall reason or to provide additional feedback to contributors.',
        required: false,
      },
      assignToMostRecentWorkerAtDestinationReviewLevel: {
        description:
          'Task will be assigned to the most recent worker at the destination review level (will not affect exclusive workers and minutes to claim for the worker). By default this is set to false, set this to true to override.',
        required: false,
      },
    }),
    [],
  );

  // Generate table data from param metadata
  const data = useMemo(
    () =>
      Object.entries(paramMetadata).map(([columnName, { description, required }]) => ({
        columnName,
        description: required ? `(Required) - ${description}` : `(Optional) - ${description}`,
      })),
    [paramMetadata],
  );

  // Create column helper
  const columnHelper = createColumnHelper<{ columnName: string; description: string }>();

  // Define columns and cell components
  const ColumnNameCell = ({ value }: { value: string }) => <Code>{value}</Code>;
  ColumnNameCell.displayName = 'ColumnNameCell';

  const DescriptionCell = ({ value }: { value: string }) => <>{value}</>;
  DescriptionCell.displayName = 'DescriptionCell';

  const renderColumnName = (info: any) => <ColumnNameCell value={info.getValue()} />;
  renderColumnName.displayName = 'renderColumnName';

  const renderDescription = (info: any) => <DescriptionCell value={info.getValue()} />;
  renderDescription.displayName = 'renderDescription';

  const columns = [
    columnHelper.accessor('columnName', {
      id: 'columnName',
      header: 'Column Name',
      cell: renderColumnName,
    }),
    columnHelper.accessor('description', {
      id: 'description',
      header: 'Description',
      cell: renderDescription,
    }),
  ];

  return (
    <DataTable.Root
      columns={columns}
      data={data}
      enableSorting={false}
      enableColumnFilters={false}
      initialState={{
        pagination: {
          pageIndex: 0,
          pageSize: 10,
        },
      }}
    >
      {({ table }) => (
        <DataTable.Table>
          <DataTable.Header>
            {table.getHeaderGroups().map(headerGroup => (
              <DataTable.HeaderGroup key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <DataTable.ColumnHeaderCell key={header.id} header={header} />
                ))}
              </DataTable.HeaderGroup>
            ))}
          </DataTable.Header>
          <DataTable.Body>
            {table.getRowModel().rows.map(row => (
              <DataTable.Row key={row.id} row={row}>
                {row.getVisibleCells().map(cell => (
                  <DataTable.Cell key={cell.id} cell={cell} />
                ))}
              </DataTable.Row>
            ))}
          </DataTable.Body>
        </DataTable.Table>
      )}
    </DataTable.Root>
  );
};

ParametersTable.displayName = 'ParametersTable';

export default TaskActionsCardComponent;
